import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWorkout } from '../context/WorkoutContext';
import { muscleGroupNames } from '../data/exerciseData';
import { soundManager } from '../utils/SoundManager';
import './WorkoutComplete.css';

const WorkoutComplete = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, actions, computed } = useWorkout();
  const [showConfetti, setShowConfetti] = useState(true);

  const {
    generatedWorkout,
    totalCompletedReps,
    repCount,
    setCount,
    difficultyLevel,
    selectedMuscleGroups,
    workoutType
  } = state;

  // Get trainer program info from location state or sessionStorage
  const getTrainerProgramInfo = () => {
    console.log('WorkoutComplete: Looking for trainer info...');
    console.log('Location state:', location.state);

    if (location.state?.isTrainerProgram) {
      console.log('WorkoutComplete: Found trainer info in location state:', location.state);
      return {
        isTrainerProgram: location.state.isTrainerProgram,
        trainerName: location.state.trainerName,
        programName: location.state.programName,
        intensityLevel: location.state.intensityLevel || 100,
        intensityLabel: location.state.intensityLabel || 'Advanced'
      };
    }

    try {
      const sessionData = sessionStorage.getItem('trainerProgramInfo');
      console.log('SessionStorage data:', sessionData);

      if (sessionData) {
        const parsed = JSON.parse(sessionData);
        console.log('WorkoutComplete: Found trainer info in sessionStorage:', parsed);
        return {
          isTrainerProgram: parsed.isTrainerProgram || false,
          trainerName: parsed.trainerName || null,
          programName: parsed.programName || null,
          intensityLevel: parsed.intensityLevel || 100,
          intensityLabel: parsed.intensityLabel || 'Advanced'
        };
      }
    } catch (e) {
      console.error('Error parsing trainer program info from sessionStorage:', e);
    }

    console.log('WorkoutComplete: No trainer info found, using defaults');
    return {
      isTrainerProgram: false,
      trainerName: null,
      programName: null,
      intensityLevel: 100,
      intensityLabel: 'Advanced'
    };
  };

  const trainerInfo = getTrainerProgramInfo();

  // Get current date and time
  const getCurrentDateTime = () => {
    const now = new Date();
    return now.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    });
  };

  // REMOVED AUTO-SAVE useEffect - no more automatic saving!

  // Handle confetti effect and workout complete sound on load
  useEffect(() => {
    soundManager.playSound('workoutComplete2', 0.8);
    const timer = setTimeout(() => {
      setShowConfetti(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // If no workout was completed, redirect to home
  useEffect(() => {
    if (generatedWorkout.length === 0) {
      navigate('/');
    }
  }, [generatedWorkout, navigate]);

  // Group exercises by muscle group for display
  const getExercisesByMuscleGroup = () => {
    const exercisesByMuscleGroup = {};

    selectedMuscleGroups.split('').forEach(char => {
      const muscleGroupName = muscleGroupNames[char] || 'Other';

      if (!exercisesByMuscleGroup[char]) {
        exercisesByMuscleGroup[char] = {
          name: muscleGroupName,
          exercises: []
        };
      }
    });

    generatedWorkout.forEach(exercise => {
      let assigned = false;

      for (const char of selectedMuscleGroups) {
        const muscleGroupExercises = [];

        if (!assigned && muscleGroupExercises.includes(exercise)) {
          exercisesByMuscleGroup[char].exercises.push(exercise);
          assigned = true;
        }
      }

      if (!assigned) {
        if (!exercisesByMuscleGroup['other']) {
          exercisesByMuscleGroup['other'] = {
            name: 'Other',
            exercises: []
          };
        }
        exercisesByMuscleGroup['other'].exercises.push(exercise);
      }
    });

    return exercisesByMuscleGroup;
  };

  // Calculate workout stats
  const totalExercises = generatedWorkout.length;

  // Save workout function - ONLY called when button is clicked
  const saveWorkout = () => {
    if (generatedWorkout.length === 0) {
      console.log('No workout to save');
      return;
    }

    try {
      const workoutToSave = {
        id: Date.now(),
        date: new Date().toISOString(), // Use ISO string for consistent date format
        exercises: [...generatedWorkout],
        totalReps: totalCompletedReps,
        repCount,
        setCount,
        difficultyLevel,
        muscleGroupCombination: selectedMuscleGroups,
        workoutType: workoutType || 'custom',
        timestamp: new Date().toISOString(),
        isTrainerProgram: trainerInfo.isTrainerProgram,
        trainerName: trainerInfo.trainerName,
        programName: trainerInfo.programName,
        intensityLevel: trainerInfo.intensityLevel,
        intensityLabel: trainerInfo.intensityLabel,
        difficultyDescription: trainerInfo.isTrainerProgram
            ? `${trainerInfo.intensityLabel} (${trainerInfo.intensityLevel}%)`
            : computed.difficultyDescription
      };

      const savedWorkouts = JSON.parse(localStorage.getItem('workoutHistory') || '[]');
      savedWorkouts.push(workoutToSave);
      localStorage.setItem('workoutHistory', JSON.stringify(savedWorkouts));

      console.log('Workout saved successfully when button clicked');
      return true;
    } catch (error) {
      console.error('Error saving workout:', error);
      return false;
    }
  };

  // Handle save and go home - NOW INCLUDES MANUAL SAVE
  const handleSaveAndHome = () => {
    soundManager.playSound('next', 0.6);

    // Save the workout when button is clicked
    const saveSuccess = saveWorkout();

    if (!saveSuccess) {
      alert('Error saving workout. Please try again.');
      return;
    }

    // Clear everything and go home
    actions.resetWorkout();
    actions.clearWorkoutProgress();
    actions.setMuscleGroups('');
    actions.setRepCount(10);
    actions.setSetCount(1);
    actions.setDifficultyLevel(1);
    sessionStorage.removeItem('currentWorkout');
    sessionStorage.removeItem('workoutProgress');
    sessionStorage.removeItem('trainerProgramInfo');
    navigate('/');
  };

  return (
      <div className="workout-complete-container">
        {showConfetti && <div className="confetti"></div>}

        <header className="complete-header">
          <h1>Workout Complete!</h1>
          <p className="date">{getCurrentDateTime()}</p>
          {trainerInfo.isTrainerProgram && (
              <div className="trainer-program-completion">
                <h3>"{trainerInfo.programName}"</h3>
                <p>Designed by {trainerInfo.trainerName}</p>
                <p>Completed at <strong>{trainerInfo.intensityLabel} ({trainerInfo.intensityLevel}%)</strong> intensity</p>
              </div>
          )}
        </header>

        <div className="summary-card">
          <div className="congrats-message">
            <h2>Congratulations!</h2>
            <p>You've successfully completed your workout.</p>
            <p className="email-reminder">📧 Your workout details have been sent to your email!</p>
            <p className="save-reminder">💾 Click the button below to save your workout!</p>
          </div>

          <div className="workout-stats">
            <div className="stat-item">
              <span className="stat-value">{totalExercises}</span>
              <span className="stat-label">Exercises</span>
            </div>

            <div className="stat-item">
              <span className="stat-value">{repCount}</span>
              <span className="stat-label">Reps per Exercise</span>
            </div>

            <div className="stat-item">
              <span className="stat-value">{totalCompletedReps}</span>
              <span className="stat-label">Total Reps</span>
            </div>
          </div>

          <div className="muscle-group-info">
            {trainerInfo.isTrainerProgram ? (
                <div className="trainer-info-display">
                  <h3>Trainer Program:</h3>
                  <div className="trainer-details">
                    <p><strong>Program:</strong> {trainerInfo.programName}</p>
                    <p><strong>Trainer:</strong> {trainerInfo.trainerName}</p>
                    <p><strong>Intensity:</strong> {trainerInfo.intensityLabel} ({trainerInfo.intensityLevel}%)</p>
                  </div>
                </div>
            ) : (
                <>
                  <h3>Muscle Groups:</h3>
                  <div className="muscle-group-code">
                    {selectedMuscleGroups}
                  </div>
                </>
            )}
            {workoutType && (
                <div className="workout-type-info">
                  <small>Workout Type: {workoutType}</small>
                </div>
            )}
          </div>
        </div>

        <div className="exercises-container">
          <h2>Exercises Completed</h2>

          <div className="exercise-list">
            {generatedWorkout.map((exercise, index) => (
                <div key={`${exercise}-${index}`} className="exercise-item">
                  <span className="exercise-number">{index + 1}</span>
                  <span className="exercise-name">{exercise}</span>
                  <span className="exercise-reps">{repCount} reps</span>
                </div>
            ))}
          </div>
        </div>

        <div className="action-buttons">
          <button
              className="action-button save-and-home-button"
              onClick={handleSaveAndHome}
          >
            💾 Save Workout & Back to Home 🏠
          </button>
        </div>
      </div>
  );
};

export default WorkoutComplete;